// SPDX-License-Identifier: GPL-3.0-only
// Rules adapted from pinned OpenSaga game.cpp:136, panel.cpp:466,
// gamestatus_lsw.cpp:649-747, minikits.cpp:336/585 and shop.cpp:331/755.
// PC extra prices verified against the owned PE CHEAT table (see ECONOMY_PARITY).
// This module consumes authoritative gameplay events; it does not infer them
// from scene visits, proximity, rendered objects or test-enemy deaths.

export const MAX_STUDS = 4_000_000_000;
export const STUD_VALUES = Object.freeze({ silver: 10, gold: 100, blue: 1000, purple: 10000 });
export const SAVE_VERSION = 1;
const MULTIPLIERS = Object.freeze({ scorex2: 2, scorex4: 4, scorex6: 6, scorex8: 8, scorex10: 10 });
const PC_EXTRA_ROWS = [
  ['extratoggle', 25000], ['poo', 8000], ['disguises', 10000], ['daisychains', 5000],
  ['c3pobits', 10000], ['towdeathstar', 5000], ['silhouettes', 10000], ['beepbeep', 7500],
  ['supergonk', 100000], ['poomoney', 100000], ['walkietalkiedisable', 5000], ['powerbrickdetector', 125000],
  ['superslap', 5000], ['forcezipup', 15000], ['coinmagnet', 100000], ['disarmtroopers', 100000],
  ['characterstuds', 100000], ['perfectdeflect', 20000], ['explodingblasterbolts', 20000], ['forcepull', 12000],
  ['vehiclesmartbomb', 15000], ['superastromech', 10000], ['superjedislam', 11000], ['superthermaldetonator', 25000],
  ['deflectbolts', 150000], ['darkside', 25000], ['superblasters', 15000], ['fastforce', 40000],
  ['supersabres', 40000], ['tractorbeam', 15000], ['invincibility', 1000000], ['scorex2', 1250000],
  ['selfdestruct', 25000], ['fastbuild', 30000], ['scorex4', 2500000], ['regenerate', 150000],
  ['minikitdetector', 250000], ['scorex6', 5000000], ['superzapper', 14000], ['rockets', 20000],
  ['scorex8', 10000000], ['superewokcatapult', 25000], ['infinitetorpedos', 25000], ['scorex10', 20000000],
];
export const PC_EXTRA_PRICES = Object.freeze(Object.fromEntries(PC_EXTRA_ROWS));

function integer(value, maximum = MAX_STUDS, name = 'integer') {
  if (!Number.isSafeInteger(value) || value < 0 || value > maximum) throw new Error(`Invalid ${name}.`);
  return value;
}
function id(value) {
  if (typeof value !== 'string' || !/^[a-z0-9_./:-]{1,160}$/i.test(value)) throw new Error('Invalid progression identity.');
  return value.toLowerCase();
}
function array(value, maximum, label) {
  if (!Array.isArray(value) || value.length > maximum || new Set(value).size !== value.length) throw new Error(`Invalid ${label}.`);
  return value;
}
const clone = value => JSON.parse(JSON.stringify(value));
const addUnique = (list, value) => { if (list.includes(value)) return false; list.push(value); return true; };

export function studMultiplier(enabledExtras = [], { bonus = false } = {}) {
  if (bonus) return 1;
  return [...new Set(enabledExtras)].reduce((total, extra) => total * (Object.hasOwn(MULTIPLIERS, extra) ? MULTIPLIERS[extra] : 1), 1);
}
export function increaseStuds(total, amount, multiplier = 1) {
  integer(total); integer(amount, Number.MAX_SAFE_INTEGER, 'stud amount'); integer(multiplier, 3840, 'stud multiplier');
  if (multiplier < 1) throw new Error('Invalid stud multiplier.');
  const result = BigInt(total) + BigInt(amount) * BigInt(multiplier);
  return Number(result > BigInt(MAX_STUDS) ? BigInt(MAX_STUDS) : result);
}

function lines(text) {
  if (typeof text !== 'string' || text.length > 8 * 1024 * 1024) throw new Error('Invalid economy asset text.');
  return text.split(/\r?\n/).map(line => line.replace(/\/\/.*$/, '').trim()).filter(line => line && !line.startsWith(';'));
}
function tokens(line) { return [...line.matchAll(/"([^"]*)"|([^\s]+)/g)].map(match => (match[1] ?? match[2]).toLowerCase()); }

// areaTexts is keyed by the AREAS.TXT file name (e.g. negotiations), not by a
// rendered subscene. Completion rules must be supplied by the script adapter
// after its real objectives/end transition have been resolved. Null means
// unresolved and deliberately prevents story-completion rewards.
export function parseEconomyCatalog({ areasText, areaTexts = {}, collectionText, episodesText = '', completionRules = {} }) {
  const chapters = Object.create(null), characters = Object.create(null), extras = Object.create(null), episodes = [];
  let area = null;
  for (const line of lines(areasText)) {
    const [key, ...args] = tokens(line);
    if (key === 'area_start') { if (area) throw new Error('Nested area block.'); area = { levels: [], flags: [], minikit: null, redBrickExtra: null, players: [], targets: { story: 0, freeplay: 0 }, completionRule: null }; }
    else if (key === 'area_end') {
      if (!area?.id || chapters[area.id]) throw new Error('Invalid or duplicate area.');
      const text = areaTexts[area.id];
      if (text !== undefined) for (const record of lines(text)) {
        const [command, ...parts] = tokens(record);
        if (command === 'story_coins' || command === 'freeplay_coins') area.targets[command === 'story_coins' ? 'story' : 'freeplay'] = integer(Number(parts[0]));
        if (command === 'character' && parts[1] === 'player') addUnique(area.players, id(parts[0]));
      }
      const rule = completionRules[area.id];
      if (rule) {
        const objectiveIds = array(rule.objectiveIds, 1000, 'completion objectives').map(id);
        if (!objectiveIds.length || !area.levels.includes(id(rule.level)) || typeof rule.event !== 'string' || !rule.event) throw new Error('Invalid resolved completion rule.');
        area.completionRule = { objectiveIds, level: id(rule.level), event: id(rule.event) };
      }
      chapters[area.id] = area; area = null;
    } else if (area) {
      if (key === 'file') area.id = id(args[0]);
      else if (key === 'dir') area.directory = args[0];
      else if (key === 'level') area.levels.push(id(args[0]));
      else if (key === 'minikit') area.minikit = id(args[0]);
      else if (key === 'redbrick_cheat') area.redBrickExtra = id(args[0]);
      else area.flags.push(key);
    }
  }
  if (area) throw new Error('Unterminated area block.');
  let episode = null;
  for (const line of lines(episodesText)) {
    const [key, ...args] = tokens(line);
    if (key === 'episode_start') episode = [];
    else if (key === 'area' && episode) {
      const chapter = chapters[id(args[0])];
      if (!chapter) throw new Error('Episode refers to unknown area.');
      if (chapter.flags.includes('ending_area')) episode.finished = true;
      if (!episode.finished) episode.push(chapter.id);
    } else if (key === 'episode_end') { if (!episode?.length) throw new Error('Invalid episode.'); episodes.push([...episode]); episode = null; }
  }
  if (episode) throw new Error('Unterminated episode block.');
  for (const line of lines(collectionText)) {
    const [key, name, ...rest] = tokens(line);
    if (key !== 'collect') continue;
    const characterId = id(name), record = { id: characterId, price: null, gate: 'initial', chapter: null, goldBricks: 0 };
    for (let i = 0; i < rest.length; i++) {
      const command = rest[i];
      if (command === 'story') record.gate = 'story';
      else if (command === 'area_complete') { record.gate = 'area'; record.chapter = id(rest[++i]); }
      else if (command === 'buy_in_shop') record.price = integer(Number(rest[++i]));
      else if (command === 'gold_bricks') { record.gate = 'gold-bricks'; record.goldBricks = integer(Number(rest[++i]), 10000); }
      else if (command === 'all_episodes_complete') record.gate = 'all-episodes';
      else if (command === 'all_minikits_complete') record.gate = 'all-minikits';
      else if (command === 'minikit') record.gate = 'minikit';
      else if (command === '100_percent' || command === 'in_pack') record.gate = 'unimplemented';
    }
    if (characters[characterId]) throw new Error('Duplicate collection character.');
    characters[characterId] = record;
  }
  PC_EXTRA_ROWS.forEach(([extraId, price], index) => {
    const chapter = Object.values(chapters).find(chapter => chapter.redBrickExtra === extraId);
    extras[extraId] = { id: extraId, price, requiresRedBrick: index > 7, chapter: chapter?.id ?? null };
  });
  return { chapters, characters, extras, episodes, regularChapters: episodes.flat(), shopGoldBricks: 8 };
}

function emptyChapter() { return { storyComplete: false, trueJedi: false, minikits: [], redBrick: false }; }
function newState(catalog) {
  return { version: SAVE_VERSION, bank: 0, playSeconds: 0,
    chapters: Object.fromEntries(Object.keys(catalog.chapters).map(key => [key, emptyChapter()])),
    characters: Object.values(catalog.characters).filter(character => character.gate === 'initial' && character.price === null).map(character => character.id),
    purchased: [], enabledExtras: [], goldBricks: [], unlockedChapters: catalog.episodes[0]?.slice(0, 1) ?? [], run: null };
}

export class Progression {
  #state;
  constructor(catalog, { storage = null, key = 'lego-saga-port.progression.v1' } = {}) {
    if (!catalog?.chapters || !catalog.characters || !catalog.extras) throw new Error('Invalid economy catalog.');
    if (storage && (typeof storage.getItem !== 'function' || typeof storage.setItem !== 'function')) throw new Error('Invalid save storage.');
    this.catalog = catalog; this.storage = storage; this.key = key; this.#state = newState(catalog);
  }
  snapshot() { return clone(this.#state); }
  #run() { if (!this.#state.run) throw new Error('No active chapter run.'); return this.#state.run; }
  startRun(chapterId, mode = 'story') {
    chapterId = id(chapterId);
    if (this.#state.run) throw new Error('Finish or resume the existing run first.');
    if (!this.catalog.chapters[chapterId] || !['story', 'freeplay'].includes(mode)) throw new Error('Invalid chapter run.');
    if (!this.#state.unlockedChapters.includes(chapterId)) throw new Error('Chapter is locked.');
    if (mode === 'freeplay' && !this.#state.chapters[chapterId].storyComplete) throw new Error('Free Play requires story completion.');
    this.#state.run = { chapter: chapterId, mode, studs: 0, pickupIds: [], minikits: [], redBrick: false, trueJedi: false, objectives: [], storyCharacters: [] };
    return this.snapshot().run;
  }
  collectStud(pickupId, type, count = 1) {
    const run = this.#run(); pickupId = id(pickupId);
    if (!Object.hasOwn(STUD_VALUES, type)) throw new Error('Unknown original stud type.');
    integer(count, MAX_STUDS / STUD_VALUES[type], 'stud count');
    if (count === 0) throw new Error('Stud count must be positive.');
    if (!this.recordPickup(pickupId)) return false;
    run.studs = increaseStuds(run.studs, STUD_VALUES[type] * count, studMultiplier(this.#state.enabledExtras));
    const target = this.catalog.chapters[run.chapter].targets[run.mode];
    if (target > 0 && run.studs >= target) run.trueJedi = true; // Latches even if studs are later lost.
    return true;
  }
  recordPickup(pickupId) {
    const run = this.#run(); pickupId = id(pickupId);
    if (run.pickupIds.includes(pickupId)) return false;
    if (run.pickupIds.length >= 200000) throw new Error('Run pickup ledger is full.');
    run.pickupIds.push(pickupId); return true;
  }
  loseRunStuds(amount) { const run = this.#run(); integer(amount); run.studs = Math.max(0, run.studs - amount); return run.studs; }
  collectMinikit(levelId, name) {
    const run = this.#run(), chapter = this.catalog.chapters[run.chapter]; levelId = id(levelId); name = id(name);
    if (!chapter.minikit || !chapter.levels.includes(levelId) || name.length > 8 || name.includes(':')) throw new Error('Invalid original minikit identity.');
    const identity = `${levelId}:${name}`, saved = this.#state.chapters[run.chapter].minikits;
    if (saved.includes(identity) || run.minikits.includes(identity) || saved.length + run.minikits.length >= 10) return false;
    run.minikits.push(identity); return true;
  }
  collectRedBrick() {
    const run = this.#run();
    if (!this.catalog.chapters[run.chapter].redBrickExtra) throw new Error('Chapter has no original red brick.');
    if (run.redBrick || this.#state.chapters[run.chapter].redBrick) return false;
    run.redBrick = true; return true;
  }
  recordObjective(objectiveId) {
    const run = this.#run(), rule = this.catalog.chapters[run.chapter].completionRule; objectiveId = id(objectiveId);
    if (!rule?.objectiveIds.includes(objectiveId)) throw new Error('Objective is unresolved or unknown.');
    return addUnique(run.objectives, objectiveId);
  }
  recordStoryCharacter(characterId) {
    const run = this.#run(); characterId = id(characterId);
    if (run.mode !== 'story' || this.catalog.characters[characterId]?.gate !== 'story' || !this.catalog.chapters[run.chapter].players.includes(characterId)) throw new Error('Invalid story-character event.');
    return addUnique(run.storyCharacters, characterId);
  }
  #awardGold(identity) { addUnique(this.#state.goldBricks, identity); }
  #commitRun(storyComplete) {
    const run = this.#run(), chapter = this.catalog.chapters[run.chapter], saved = this.#state.chapters[run.chapter];
    this.#state.bank = increaseStuds(this.#state.bank, run.studs);
    run.minikits.forEach(identity => addUnique(saved.minikits, identity));
    if (run.redBrick) saved.redBrick = true;
    if (run.trueJedi && !saved.trueJedi && chapter.minikit) { saved.trueJedi = true; this.#awardGold(`${run.chapter}:true-jedi`); }
    if (saved.minikits.length === 10 && !this.#state.goldBricks.includes(`${run.chapter}:minikit`)) {
      this.#awardGold(`${run.chapter}:minikit`);
      this.#state.bank = increaseStuds(this.#state.bank, 50000); // Status reward is not multiplied.
      if (this.catalog.characters[chapter.minikit]) addUnique(this.#state.characters, chapter.minikit);
    }
    if (storyComplete && !saved.storyComplete) {
      saved.storyComplete = true;
      if (!chapter.flags.includes('no_gold_brick')) this.#awardGold(`${run.chapter}:story`);
      run.storyCharacters.forEach(character => addUnique(this.#state.characters, character));
      for (const episode of this.catalog.episodes) {
        const index = episode.indexOf(run.chapter);
        if (index >= 0 && index + 1 < episode.length) addUnique(this.#state.unlockedChapters, episode[index + 1]);
      }
      if (run.chapter === this.catalog.episodes[0]?.[0]) this.catalog.episodes.forEach(episode => addUnique(this.#state.unlockedChapters, episode[0]));
    }
    const kits = Object.values(this.catalog.chapters).filter(chapter => chapter.minikit);
    if (kits.length && kits.every(chapter => this.#state.chapters[chapter.id].minikits.length === 10)) {
      Object.values(this.catalog.characters).filter(character => character.gate === 'all-minikits' && character.price === null).forEach(character => addUnique(this.#state.characters, character.id));
    }
    this.#state.run = null;
    return this.snapshot();
  }
  saveAndExit() { return this.#commitRun(false); }
  completeChapter(proof) {
    const run = this.#run(), rule = this.catalog.chapters[run.chapter].completionRule;
    if (!rule || !proof || proof.level !== rule.level || proof.event !== rule.event || !rule.objectiveIds.every(objective => run.objectives.includes(objective))) throw new Error('Original scripted completion criteria have not been satisfied.');
    return this.#commitRun(run.mode === 'story');
  }
  advancePlayTime(seconds) {
    if (!Number.isFinite(seconds) || seconds < 0 || seconds > 3600) throw new Error('Invalid play-time increment.');
    this.#state.playSeconds = Math.min(1e9, this.#state.playSeconds + seconds);
  }
  canPurchase(itemId) {
    itemId = id(itemId);
    if (this.#state.purchased.includes(itemId)) return false;
    const fields = itemId.split(':');
    if (fields.length !== 2) return false;
    const [kind, name] = fields;
    if (kind === 'extra') {
      const extra = this.catalog.extras[name];
      return !!extra && this.#state.bank >= extra.price && (!extra.requiresRedBrick || !!this.#state.chapters[extra.chapter]?.redBrick);
    }
    if (kind === 'gold') { const index = Number(name); return /^(0|[1-9]\d*)$/.test(name) && index < this.catalog.shopGoldBricks && this.#state.playSeconds >= index * 3600 && this.#state.bank >= 10000 + index * 5000; }
    if (kind !== 'character') return false;
    const character = this.catalog.characters[name];
    if (!character || character.price === null || this.#state.characters.includes(name) || this.#state.bank < character.price) return false;
    if (character.gate === 'initial') return true;
    if (character.gate === 'area') return !!this.#state.chapters[character.chapter]?.storyComplete;
    if (character.gate === 'all-episodes') return this.catalog.episodes.length > 0 && this.catalog.regularChapters.every(chapter => this.#state.chapters[chapter].storyComplete);
    if (character.gate === 'all-minikits') return Object.values(this.catalog.chapters).filter(chapter => chapter.minikit).every(chapter => this.#state.chapters[chapter.id].minikits.length === 10);
    if (character.gate === 'gold-bricks') return this.#state.goldBricks.length >= character.goldBricks;
    return false;
  }
  purchase(itemId) {
    itemId = id(itemId);
    if (!this.canPurchase(itemId)) return false;
    const [kind, name] = itemId.split(':');
    const price = kind === 'extra' ? this.catalog.extras[name].price : kind === 'character' ? this.catalog.characters[name].price : 10000 + Number(name) * 5000;
    this.#state.bank -= price; this.#state.purchased.push(itemId);
    if (kind === 'character') addUnique(this.#state.characters, name);
    if (kind === 'gold') this.#awardGold(`shop:${Number(name)}`);
    return true;
  }
  setExtraEnabled(extraId, enabled) {
    extraId = id(extraId);
    if (typeof enabled !== 'boolean' || !this.#state.purchased.includes(`extra:${extraId}`)) throw new Error('Extra must be purchased before enabling.');
    if (enabled) addUnique(this.#state.enabledExtras, extraId);
    else this.#state.enabledExtras = this.#state.enabledExtras.filter(extra => extra !== extraId);
  }
  save() {
    if (!this.storage) throw new Error('Save storage is unavailable.');
    // Storage exceptions propagate: never pretend a quota/security failure saved.
    const text = JSON.stringify(this.#state);
    if (text.length > 16 * 1024 * 1024) throw new Error('Progression save exceeds the supported size.');
    this.storage.setItem(this.key, text); return true;
  }
  load() {
    if (!this.storage) throw new Error('Save storage is unavailable.');
    const text = this.storage.getItem(this.key); if (text === null) return false;
    this.restore(text); return true;
  }
  restore(text) {
    if (typeof text !== 'string' || text.length > 16 * 1024 * 1024) throw new Error('Invalid progression save.');
    let state;
    try { state = JSON.parse(text); } catch { throw new Error('Invalid progression save JSON.'); }
    validateState(state, this.catalog); // Validate everything before replacing a live save.
    this.#state = clone(state); return true;
  }
}

function validateState(state, catalog) {
  if (!state || Array.isArray(state) || state.version !== SAVE_VERSION) throw new Error('Unsupported progression save version.');
  const keys = ['version', 'bank', 'playSeconds', 'chapters', 'characters', 'purchased', 'enabledExtras', 'goldBricks', 'unlockedChapters', 'run'];
  if (Object.keys(state).length !== keys.length || keys.some(key => !Object.hasOwn(state, key))) throw new Error('Invalid save fields.');
  integer(state.bank);
  if (!Number.isFinite(state.playSeconds) || state.playSeconds < 0 || state.playSeconds > 1e9) throw new Error('Invalid saved play time.');
  if (!state.chapters || Object.keys(state.chapters).length !== Object.keys(catalog.chapters).length) throw new Error('Saved chapter catalog differs.');
  for (const [chapterId, chapter] of Object.entries(catalog.chapters)) {
    const saved = state.chapters[chapterId];
    if (!saved || Object.keys(saved).length !== 4 || ['storyComplete', 'trueJedi', 'redBrick'].some(key => typeof saved[key] !== 'boolean')) throw new Error('Invalid saved chapter.');
    array(saved.minikits, 10, 'saved minikits').forEach(identity => validateMinikit(identity, chapter));
    if (!chapter.minikit && (saved.minikits.length || saved.trueJedi)) throw new Error('Chapter has no collectible progression.');
    if (!chapter.redBrickExtra && saved.redBrick) throw new Error('Chapter has no red brick.');
  }
  array(state.characters, Object.keys(catalog.characters).length, 'saved characters').forEach(character => { if (!catalog.characters[character]) throw new Error('Unknown saved character.'); });
  array(state.purchased, 1000, 'saved purchases').forEach(item => {
    if (typeof item !== 'string') throw new Error('Invalid saved purchase.');
    const [kind, name, extra] = item.split(':');
    if (extra !== undefined || (kind === 'extra' ? !catalog.extras[name] : kind === 'character' ? catalog.characters[name]?.price === null || !catalog.characters[name] : kind !== 'gold' || !/^(0|[1-9]\d*)$/.test(name) || Number(name) >= catalog.shopGoldBricks)) throw new Error('Unknown saved purchase.');
    if (kind === 'character' && !state.characters.includes(name)) throw new Error('Purchased character missing from collection.');
  });
  array(state.enabledExtras, Object.keys(catalog.extras).length, 'enabled extras').forEach(extra => { if (!state.purchased.includes(`extra:${extra}`)) throw new Error('Unpurchased saved extra.'); });
  array(state.unlockedChapters, Object.keys(catalog.chapters).length, 'unlocked chapters').forEach(chapter => { if (!catalog.chapters[chapter]) throw new Error('Unknown unlocked chapter.'); });
  array(state.goldBricks, Object.keys(catalog.chapters).length * 3 + catalog.shopGoldBricks, 'saved gold bricks').forEach(identity => {
    if (typeof identity !== 'string') throw new Error('Invalid saved gold brick.');
    const [chapter, reason, extra] = identity.split(':');
    if (extra !== undefined) throw new Error('Invalid saved gold-brick identity.');
    if (chapter === 'shop') { if (!/^(0|[1-9]\d*)$/.test(reason) || Number(reason) >= catalog.shopGoldBricks || !state.purchased.includes(`gold:${reason}`)) throw new Error('Unpurchased gold brick.'); }
    else if (!catalog.chapters[chapter] || !['story', 'true-jedi', 'minikit'].includes(reason) ||
      (reason === 'story' ? !state.chapters[chapter].storyComplete || catalog.chapters[chapter].flags.includes('no_gold_brick') : reason === 'true-jedi' ? !state.chapters[chapter].trueJedi : state.chapters[chapter].minikits.length !== 10)) throw new Error('Unearned saved gold brick.');
  });
  // The reward ledger and saved milestones must agree in both directions;
  // otherwise restoring a truncated ledger could award the same reward again.
  for (const [chapterId, chapter] of Object.entries(catalog.chapters)) {
    const saved = state.chapters[chapterId];
    if ((saved.storyComplete && !chapter.flags.includes('no_gold_brick') && !state.goldBricks.includes(`${chapterId}:story`)) ||
      (saved.trueJedi && !state.goldBricks.includes(`${chapterId}:true-jedi`)) ||
      (saved.minikits.length === 10 && !state.goldBricks.includes(`${chapterId}:minikit`))) throw new Error('Saved milestone is missing its gold-brick ledger entry.');
  }
  for (const item of state.purchased) {
    const [kind, name] = item.split(':');
    if (kind === 'gold' && !state.goldBricks.includes(`shop:${name}`)) throw new Error('Purchased gold brick is missing from its ledger.');
    if (kind === 'extra') {
      const extra = catalog.extras[name];
      if (extra.requiresRedBrick && !state.chapters[extra.chapter]?.redBrick) throw new Error('Purchased extra is missing its required red brick.');
    }
  }
  if (state.run !== null) {
    const run = state.run, chapter = catalog.chapters[run?.chapter];
    if (!chapter || Object.keys(run).length !== 9 || !['story', 'freeplay'].includes(run.mode) || !state.unlockedChapters.includes(run.chapter) || (run.mode === 'freeplay' && !state.chapters[run.chapter].storyComplete)) throw new Error('Invalid saved active run.');
    integer(run.studs);
    if (typeof run.redBrick !== 'boolean' || typeof run.trueJedi !== 'boolean' || (run.redBrick && !chapter.redBrickExtra) || (run.trueJedi && chapter.targets[run.mode] === 0)) throw new Error('Invalid run collectible state.');
    array(run.pickupIds, 200000, 'run pickup ledger').forEach(identity => { if (id(identity) !== identity) throw new Error('Noncanonical saved pickup identity.'); });
    array(run.minikits, 10, 'run minikits').forEach(identity => {
      validateMinikit(identity, chapter);
      if (state.chapters[run.chapter].minikits.includes(identity)) throw new Error('Run repeats a saved minikit.');
    });
    if (run.minikits.length + state.chapters[run.chapter].minikits.length > 10) throw new Error('Run exceeds original minikit count.');
    array(run.objectives, 1000, 'run objectives').forEach(objective => { if (!chapter.completionRule?.objectiveIds.includes(objective)) throw new Error('Unknown saved objective.'); });
    array(run.storyCharacters, 1000, 'run story characters').forEach(character => { if (run.mode !== 'story' || catalog.characters[character]?.gate !== 'story' || !chapter.players.includes(character)) throw new Error('Invalid saved story character.'); });
  }
}
function validateMinikit(identity, chapter) {
  if (!chapter.minikit || typeof identity !== 'string') throw new Error('Invalid minikit identity.');
  const [level, name, extra] = identity.split(':');
  if (extra !== undefined || !chapter.levels.includes(level) || !name || name.length > 8 || id(name) !== name) throw new Error('Invalid original saved minikit identity.');
}
